<template>
    <div class="notifications-dropdown">
        <div class="notifications-dropdown-header">
            <span class="notifications-title">{{ $t("Notifications") }}</span>
            <div class="notifications-header-actions">
                <ActionIconButton
                    class="notifications-do-not-disturb"
                    :icon="doNotDisturb ? 'fa-regular fa-bell-slash' : 'fa-regular fa-bell'"
                    :style-type="doNotDisturb ? 'primary-brand-soft' : 'neutral-transparent'"
                    size="sm"
                    :aria-label="$t('Do not disturb')"
                    :aria-pressed="doNotDisturb"
                    :title="doNotDisturb ? $t('Resume notification alerts') : $t('Pause notification alerts')"
                    @click="toggleDoNotDisturb"
                />
                <span v-if="notificationsCount > 0" class="notifications-count">{{ notificationsCountLabel }}</span>
            </div>
        </div>

        <div v-if="loading" class="notifications-dropdown-status">
            <LoadingState :text="$t('Loading notifications')" />
        </div>

        <div v-else-if="visibleNotifications.length === 0" class="notifications-dropdown-status">
            <i class="fa-regular fa-bell-slash"></i>
            <span>{{ $t("There are no new notifications") }}</span>
        </div>

        <div v-else class="notifications-dropdown-content">
            <div v-for="notification in visibleNotifications" :key="notification.id" class="notification-item-content">
                <div class="icon-section">
                    <div :class="['notification-icon', 'accent-' + renderNotificationAccent(notification.type)]">
                        <i :class="renderNotificationIcon(notification.type)"></i>
                    </div>
                </div>
                <div class="notification-content">
                    <div class="notification-main-content">
                        <div class="notification-category">
                            <span :title="renderNotificationType(notification.type)">
                                {{ renderNotificationType(notification.type) }}
                            </span>
                            <span v-if="!notification.read" class="notification-unread-dot"></span>
                        </div>
                        <div
                            class="notification-text"
                            :class="{ 'notification-text-link': !!getNotificationLink(notification) }"
                            @click="onNotificationClick(notification)"
                        >
                            <span :title="renderNotificationMessage(notification)">
                                {{ renderNotificationMessage(notification) }}
                            </span>
                        </div>
                    </div>
                    <div class="notification-meta">
                        <div class="notification-info">
                            <i class="fa-regular fa-clock"></i>
                            <span>{{ renderNotificationTime(notification.timestamp) }}</span>
                        </div>
                        <div class="notification-info">
                            <i class="fa-regular fa-user"></i>
                            <span :title="renderNotificationAuthor(notification)">
                                {{ renderNotificationAuthor(notification) }}
                            </span>
                        </div>
                    </div>
                </div>
                <ActionIconButton
                    class="notification-mark-read-action"
                    icon="fas fa-check"
                    icon-class="icon-color"
                    :style-type="'neutral-transparent-subtle'"
                    size="xl"
                    :title="$t('Mark as read')"
                    :aria-label="$t('Mark as read')"
                    @click="markOneAsRead(notification)"
                />
            </div>
        </div>

        <div class="notifications-dropdown-footer">
            <ActionButton
                class="dropdown-footer-button"
                :text="$t('Mark all as read')"
                :size="'sm'"
                icon-position="left"
                icon="fas fa-check"
                :disabled="busyMarkingAsRead || notifications.length === 0"
                :is-loading="busyMarkingAsRead"
                :loading-text="$t('Marking') + '...'"
                @click="markAllAsRead"
            />
            <ActionButton
                class="dropdown-footer-button"
                :text="$t('View all')"
                :size="'sm'"
                icon-position="left"
                icon="fa-solid fa-list-ul"
                :disabled="busyMarkingAsRead"
                @click="goToNotificationsPage"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import { renderDateAndTime } from "@/utils/time-utils";
import { withReturnTo } from "@/utils/return-navigation";
import { getNotificationContent, getNotificationMessage, getNotificationAuthor, getNotificationLink } from "@/utils/notification-content";
import { Timeouts } from "@/utils/timeout";
import { NotificationsController } from "@/control/notifications";
import { NotificationItem } from "@/api/definitions";
import { ApiNotifications } from "@/api/api-group-notifications";
import { HOME_ROUTE } from "@/app-events-plugin";
import ActionButton from "@/components/buttons/ActionButton.vue";
import { defineComponent } from "vue";
import LoadingState from "@/components/loading/LoadingState.vue";
import ActionIconButton from "@/components/buttons/ActionIconButton.vue";

export default defineComponent({
    components: {
        ActionButton,
        ActionIconButton,
        LoadingState,
    },
    name: "NotificationsDropdown",
    emits: ["close", "notifications-count-change"],
    props: {
        notificationsCount: {
            type: Number,
            default: 0,
        },
    },
    setup: function () {
        return {
            loadRequestId: getUniqueStringId(),
            markAsReadRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            doNotDisturb: NotificationsController.DoNotDisturb,
            loading: false,
            notifications: [] as NotificationItem[],
            busyMarkingAsRead: false,
        };
    },
    computed: {
        visibleNotifications: function (): NotificationItem[] {
            return this.notifications.slice(0, 25);
        },

        notificationsCountLabel: function (): string {
            return this.notificationsCount > 99 ? "+99" : this.notificationsCount + "";
        },
    },
    methods: {
        toggleDoNotDisturb: function () {
            NotificationsController.SetDoNotDisturb(!this.doNotDisturb);
        },

        goToNotificationsPage: function () {
            this.$emit("close");
            this.$router.push({ name: "notifications" });
        },

        renderNotificationIcon: function (type: string) {
            return getNotificationContent(type, this.$t).icon;
        },

        renderNotificationAccent: function (type: string) {
            return getNotificationContent(type, this.$t).accent;
        },

        renderNotificationType: function (type: string) {
            return getNotificationContent(type, this.$t).title;
        },

        renderNotificationMessage: function (notification: NotificationItem) {
            return getNotificationMessage(notification, this.$t);
        },

        renderNotificationAuthor: function (notification: NotificationItem) {
            return getNotificationAuthor(notification, this.$t);
        },

        renderNotificationTime: function (timestamp: number) {
            return renderDateAndTime(timestamp, this.$t);
        },

        markAllAsRead: function () {
            if (this.busyMarkingAsRead || this.notifications.length === 0) return;

            const previousNotificationsCount = this.notificationsCount;

            this.busyMarkingAsRead = true;
            this.$emit("notifications-count-change", 0, { optimistic: true });

            Request.Pending(this.markAsReadRequestId, ApiNotifications.MarkAsRead())
                .onSuccess(() => {
                    this.busyMarkingAsRead = false;
                    this.notifications = [];
                    this.$emit("notifications-count-change", 0, { optimistic: false });
                    NotificationsController.CheckNotifications();
                    this.$emit("close");
                })
                .onRequestError((err, handleErr) => {
                    this.busyMarkingAsRead = false;
                    this.$emit("notifications-count-change", previousNotificationsCount);
                    handleErr(err, {
                        unauthorized: () => this.$requireLogin(),
                        forbidden: () => {
                            this.$showErrorModal({
                                title: this.$t("Access denied"),
                                message: this.$t("You lack the required permission to visit this page"),
                            });
                        },
                        serverError: () => {
                            this.$showErrorModal({ message: this.$t("Internal server error") });
                        },
                        networkError: () => {
                            this.$showErrorModal({ message: this.$t("Could not connect to the server") });
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.busyMarkingAsRead = false;
                    this.$emit("notifications-count-change", previousNotificationsCount);
                    console.error(err);
                    this.$showErrorModal({ message: err.message });
                });
        },

        markOneAsRead: function (notification: NotificationItem) {
            if (notification.read) return;

            Request.Pending(getUniqueStringId(), ApiNotifications.MarkOneAsRead({ id: notification.id }))
                .onSuccess(() => {
                    this.notifications = this.notifications.filter((n) => n.id !== notification.id);
                    const newCount = Math.max(0, this.notificationsCount - 1);
                    this.$emit("notifications-count-change", newCount, { optimistic: false });
                    NotificationsController.CheckNotifications();
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => this.$requireLogin(),
                        forbidden: () => {
                            this.$showErrorModal({
                                title: this.$t("Access denied"),
                                message: this.$t("You lack the required permission to visit this page"),
                            });
                        },
                        badRequest: () => {
                            this.$showErrorModal({ message: this.$t("Bad request") });
                        },
                        notFound: () => {
                            notification.read = true;
                        },
                        serverError: () => this.$showErrorModal({ message: this.$t("Internal server error") }),
                        networkError: () => this.$showErrorModal({ message: this.$t("Could not connect to the server") }),
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                });
        },

        getNotificationLink: function (notification: NotificationItem) {
            return getNotificationLink(notification);
        },

        onNotificationClick: function (notification: NotificationItem) {
            const link = this.getNotificationLink(notification);
            this.markOneAsRead(notification);
            if (link) {
                this.$emit("close");
                this.$router.push(withReturnTo(link, this.$route));
            }
        },

        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            Request.Pending(
                this.loadRequestId,
                ApiNotifications.ListNotifications({
                    filterUnread: true,
                    limit: 25,
                }),
            )
                .onSuccess((response) => {
                    this.loading = false;
                    this.notifications = response.list;

                    if (this.notificationsCount === 0 && response.list.length > 0) {
                        this.$emit("notifications-count-change", response.list.length, { optimistic: false });
                    }
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.loading = false;
                            this.$requireLogin();
                        },
                        forbidden: () => {
                            this.loading = false;
                            this.$showErrorModal({
                                title: this.$t("Access denied"),
                                message: this.$t("You lack the required permission to visit this page"),
                            });
                            this.$emit("close");
                            this.$router.push({ name: HOME_ROUTE });
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("notification-do-not-disturb", (enabled) => {
            this.doNotDisturb = enabled;
        });

        this.load();

        if (typeof NotificationsController.CheckNotifications === "function") {
            NotificationsController.CheckNotifications();
        }
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
        Request.Abort(this.markAsReadRequestId);
    },
    watch: {
        notificationsCount: function () {
            if (this.busyMarkingAsRead) return;

            this.load();
        },
    },
});
</script>

<style scoped>
@import "@/style/components/dropdowns.css";
</style>
